import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore, useWorldsStore } from '../store';
import { useSidebarStore } from '../store/ui';
import { useIsMobile } from '../hooks';
import { logout } from '../api';
import { World } from '../types';
import { formatRelativeTime } from '../utils';

interface SidebarProps {
  worlds: World[];
  currentWorldId?: string;
  onCreateWorld: () => void;
  onImportWorld: () => void;
  onOpenSettings: () => void;
}

export function Sidebar({ worlds, currentWorldId, onCreateWorld, onImportWorld, onOpenSettings }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore(state => state.user);
  const logoutStore = useAuthStore(state => state.logout);
  const isAdmin = !!user?.is_admin;
  const isVerified = isAdmin || !!user?.email_verified;
  const [showNewMenu, setShowNewMenu] = useState(false);
  const newMenuRef = useRef<HTMLDivElement>(null);

  const isMobile = useIsMobile();
  const sidebarOpen = useSidebarStore(state => state.sidebarOpen);
  const setSidebarOpen = useSidebarStore(state => state.setSidebarOpen);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (newMenuRef.current && !newMenuRef.current.contains(e.target as Node)) {
        setShowNewMenu(false);
      }
    };
    if (showNewMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNewMenu]);

  // Lock body scroll when drawer is open on mobile
  useEffect(() => {
    if (isMobile && sidebarOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isMobile, sidebarOpen]);

  const handleLogout = async () => {
    await logout();
    logoutStore();
    navigate('/login');
  };

  const handleOpenProfile = () => {
    navigate('/account', { state: { backgroundLocation: location } });
    if (isMobile) setSidebarOpen(false);
  };

  // Only close sidebar on navigation when on mobile
  const closeSidebarOnMobile = () => {
    if (isMobile) setSidebarOpen(false);
  };

  const sidebarContent = (
    <aside className="w-64 min-w-[16rem] bg-dark-800 border-r border-dark-500 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-dark-500">
        <Link to="/" className="flex items-center gap-2" onClick={closeSidebarOnMobile}>
          <img src="/vite.svg" alt="Textborne logo" className="h-6 w-6" />
          <span className="text-xl font-bold text-primary-400">Textborne</span>
        </Link>
      </div>

      {/* World list */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-dark-200 uppercase tracking-wider">Worlds</h2>
          <div className="relative" ref={newMenuRef}>
            <button
              onClick={() => isVerified && setShowNewMenu(!showNewMenu)}
              className={`text-dark-200 hover:text-dark-50 transition-colors ${!isVerified ? 'opacity-40 cursor-not-allowed' : ''}`}
              disabled={!isVerified}
              title={!isVerified ? 'Verify your email to create worlds' : 'Create or import world'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
            </button>
            {showNewMenu && (
              <div className="absolute right-0 top-full mt-1 w-44 bg-dark-700 border border-dark-500 rounded-lg shadow-lg z-20 py-1">
                <button
                  onClick={() => { setShowNewMenu(false); onCreateWorld(); closeSidebarOnMobile(); }}
                  className="w-full px-3 py-2 text-left text-sm text-dark-200 hover:bg-dark-600 hover:text-dark-50 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create New
                </button>
                <button
                  onClick={() => { setShowNewMenu(false); onImportWorld(); closeSidebarOnMobile(); }}
                  className="w-full px-3 py-2 text-left text-sm text-dark-200 hover:bg-dark-600 hover:text-dark-50 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Import from Chat
                </button>
              </div>
            )}
          </div>
        </div>

        <nav className="space-y-1">
          {worlds.map(world => {
            const worldTimestamps = world as World & {
              lastAccessedAt?: string | null;
              updatedAt?: string;
              createdAt?: string;
            };
            const lastAccessedLabel = formatRelativeTime(
              world.last_accessed_at ??
                worldTimestamps.lastAccessedAt ??
                world.updated_at ??
                worldTimestamps.updatedAt ??
                world.created_at ??
                worldTimestamps.createdAt
            );

            return (
            <Link
              key={world.id}
              to={`/world/${world.id}`}
              onClick={closeSidebarOnMobile}
              className={`block px-3 py-2 rounded-lg transition-colors ${
                world.id === currentWorldId
                  ? 'bg-primary-600 text-white'
                  : 'text-dark-200 hover:bg-dark-600 hover:text-dark-50'
              }`}
            >
              <div className="font-medium truncate">{world.title}</div>
              <div className={`text-xs ${world.id === currentWorldId ? 'text-primary-200' : 'text-dark-400'}`}>
                Last accessed {lastAccessedLabel}
              </div>
            </Link>
            );
          })}

          {worlds.length === 0 && (
            <p className="text-dark-400 text-sm px-3 py-2">
              No worlds yet. Create one to start!
            </p>
          )}
        </nav>

        {isAdmin && (
          <div className="mt-6">
            <div className="text-xs font-semibold text-dark-400 uppercase tracking-wider mb-2">Admin</div>
            <Link
              to="/admin"
              onClick={closeSidebarOnMobile}
              className="block px-3 py-2 rounded-lg text-dark-200 hover:bg-dark-600 hover:text-dark-50 transition-colors"
            >
              Dashboard
            </Link>
          </div>
        )}
      </div>

      {/* User section */}
      <div className="p-4 border-t border-dark-500">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={handleOpenProfile}
            className="flex items-center gap-2 min-w-0 text-left hover:bg-dark-600/40 rounded-lg px-2 py-1 -ml-2 transition-colors"
            title="Open profile"
          >
            <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white font-medium overflow-hidden">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="h-full w-full object-cover" />
              ) : (
                user?.display_name?.[0] || user?.email?.[0] || '?'
              )}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-dark-50 truncate">
                {user?.display_name || user?.email?.split('@')[0]}
              </div>
              <div className="text-xs text-dark-400 truncate">{user?.email}</div>
            </div>
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { onOpenSettings(); closeSidebarOnMobile(); }}
              className="text-dark-300 hover:text-dark-50 transition-colors"
              title="Settings"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            <button
              onClick={handleLogout}
              className="text-dark-300 hover:text-dark-50 transition-colors"
              title="Sign out"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </aside>
  );

  // Desktop: render sidebar inline; collapse to zero width when closed
  // (in-world auto-collapse). Inner aside keeps its 256px width so the
  // slide is smooth; parent overflow-hidden clips.
  if (!isMobile) {
    return (
      <div
        className={`shrink-0 overflow-hidden transition-[width] duration-200 ease-out ${
          sidebarOpen ? 'w-64' : 'w-0'
        }`}
        aria-hidden={!sidebarOpen}
      >
        {sidebarContent}
      </div>
    );
  }

  // Mobile: render as fixed drawer with backdrop
  return (
    <>
      {/* Backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      {/* Drawer */}
      <div
        className={`fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {sidebarContent}
      </div>
    </>
  );
}
